'use client'

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createPositionGroup,
  deletePositionGroup,
  getPositionGroups,
  getPositionsView,
  reorderPositionsView,
  setPositionGroupOrder,
  updatePositionGroup,
} from '@/service/position-group.api'
import {
  type CreatePositionGroupRequest,
  type DeletePositionGroupRequest,
  type GetPositionGroupsResponse,
  type GetPositionsViewResponse,
  type PositionGroupMutationResponse,
  PositionViewMode,
  type ReorderPositionsViewRequest,
  type SetPositionGroupOrderRequest,
  type UpdatePositionGroupRequest,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

/** Account filter as a stable cache-key segment — sorted, deduped; '' means all accounts. */
function accountFilterKey(accountIds: readonly string[]): string {
  return [...new Set(accountIds)].sort().join(',')
}

export const positionGroupKeys = {
  all: ['positionGroups'] as const,
  // Groups belong to the user, not an account — one list per user.
  groups: () => [...positionGroupKeys.all, 'groups'] as const,
  viewsForMode: (mode: PositionViewMode) => [...positionGroupKeys.all, 'view', mode] as const,
  // The account ids are a FILTER: the server computes the view and every group metric from
  // those accounts only. Empty = all accounts.
  view: (mode: PositionViewMode, accountIds: readonly string[]) =>
    [...positionGroupKeys.viewsForMode(mode), accountFilterKey(accountIds)] as const,
}

const isFilteredViewKey = (queryKey: readonly unknown[]) => queryKey[3] !== ''

const invalidateFilteredViews = (queryClient: QueryClient, mode: PositionViewMode) =>
  queryClient.invalidateQueries({
    queryKey: positionGroupKeys.viewsForMode(mode),
    predicate: (query) => isFilteredViewKey(query.queryKey),
  })

/**
 * Stores an all-accounts view (the reorder echo) and refetches every filtered view of the
 * same mode — those are computed server-side from a subset of accounts, so the unfiltered
 * response can't stand in for them.
 */
export function applyUnfilteredPositionsView(
  queryClient: QueryClient,
  view: GetPositionsViewResponse
) {
  queryClient.setQueryData(positionGroupKeys.view(view.mode, []), view)
  invalidateFilteredViews(queryClient, view.mode)
}

const FILTERED_VIEW_REFRESH_DEBOUNCE_MS = 1000
const filteredViewRefreshTimers = new Map<PositionViewMode, ReturnType<typeof setTimeout>>()

/**
 * Same as applyUnfilteredPositionsView for a WebSocket push, which arrives after every
 * position delta: the filtered refetch is debounced so a burst of pushes costs one request
 * per filtered view, not one per push.
 */
export function applyPushedPositionsView(queryClient: QueryClient, view: GetPositionsViewResponse) {
  queryClient.setQueryData(positionGroupKeys.view(view.mode, []), view)
  const pending = filteredViewRefreshTimers.get(view.mode)
  if (pending) clearTimeout(pending)
  filteredViewRefreshTimers.set(
    view.mode,
    setTimeout(() => {
      filteredViewRefreshTimers.delete(view.mode)
      invalidateFilteredViews(queryClient, view.mode)
    }, FILTERED_VIEW_REFRESH_DEBOUNCE_MS)
  )
}

const invalidateGroupsAndCustomViews = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: positionGroupKeys.groups() })
  queryClient.invalidateQueries({
    queryKey: positionGroupKeys.viewsForMode(PositionViewMode.POSITION_VIEW_MODE_CUSTOM),
  })
}

export const useGetPositionGroups = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<GetPositionGroupsResponse, ErrorResponse>({
    queryKey: positionGroupKeys.groups(),
    queryFn: () => getPositionGroups({}),
    enabled,
    staleTime: Infinity,
  })
}

/** `accountIds` filters the view (and its group metrics); pass [] for all accounts. */
export const useGetPositionsView = (
  mode: PositionViewMode,
  accountIds: readonly string[],
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery<GetPositionsViewResponse, ErrorResponse>({
    queryKey: positionGroupKeys.view(mode, accountIds),
    queryFn: () => getPositionsView({ accountId: '', accountIds: [...accountIds], mode }),
    enabled,
    staleTime: Infinity,
  })
}

export const useCreatePositionGroup = (props?: {
  onSuccess?: (data: PositionGroupMutationResponse, variables: CreatePositionGroupRequest) => void
  onError?: (error: ErrorResponse, variables: CreatePositionGroupRequest) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: CreatePositionGroupRequest) => createPositionGroup(req),
    onSuccess: (data, req) => {
      invalidateGroupsAndCustomViews(queryClient)
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

export const useUpdatePositionGroup = (props?: {
  onSuccess?: (data: PositionGroupMutationResponse, variables: UpdatePositionGroupRequest) => void
  onError?: (error: ErrorResponse, variables: UpdatePositionGroupRequest) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: UpdatePositionGroupRequest) => updatePositionGroup(req),
    onSuccess: (data, req) => {
      invalidateGroupsAndCustomViews(queryClient)
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

export const useDeletePositionGroup = (props?: {
  onSuccess?: (data: PositionGroupMutationResponse, variables: DeletePositionGroupRequest) => void
  onError?: (error: ErrorResponse, variables: DeletePositionGroupRequest) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: DeletePositionGroupRequest) => deletePositionGroup(req),
    onSuccess: (data, req) => {
      invalidateGroupsAndCustomViews(queryClient)
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

// Reorder the interleaved top-level list. One order is saved per user, and the response IS
// the CUSTOM view rebuilt across ALL accounts — store it as the unfiltered view and refetch
// any filtered one.
export const useReorderPositionsView = (props?: {
  onSuccess?: (data: GetPositionsViewResponse, variables: ReorderPositionsViewRequest) => void
  onError?: (error: ErrorResponse, variables: ReorderPositionsViewRequest) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: ReorderPositionsViewRequest) => reorderPositionsView(req),
    onSuccess: (data, req) => {
      applyUnfilteredPositionsView(queryClient, data)
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

// Reorder a group's legs. The caller applies the new order to local state
// optimistically (with rollback on failure), so this intentionally does NOT
// invalidate the `groups` query: that GET's read-model can lag behind the
// write and would otherwise stomp the optimistic order with the stale one a
// moment later. The CUSTOM views are still invalidated to pick up the
// reassembled top-level entries.
export const useSetPositionGroupOrder = (props?: {
  onSuccess?: (data: PositionGroupMutationResponse, variables: SetPositionGroupOrderRequest) => void
  onError?: (error: ErrorResponse, variables: SetPositionGroupOrderRequest) => void
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (req: SetPositionGroupOrderRequest) => setPositionGroupOrder(req),
    onSuccess: (data, req) => {
      queryClient.invalidateQueries({
        queryKey: positionGroupKeys.viewsForMode(PositionViewMode.POSITION_VIEW_MODE_CUSTOM),
      })
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}
